import React, {useEffect, useState} from "react";
import {Button, ButtonLink, ButtonRow, Card, OpenExternalLinkIcon, PageHead, StickyHeader} from "@myob/myob-widgets";
import SelectionMessage from "./components/SelectionMessage";
import AccountsTable from "./components/AccountsTable";
import {FullStory} from "@fullstory/browser";
import PropTypes from "prop-types";
import * as _ from 'lodash';
import helpUrls from "../../../HelpUrls";

function SelectAccountsContainer(
  {
    accounts,
    bankfeedLimit,
    availableFeeds,
    productRedirectUrl,
    subscriptionUpgradeUrl,
    onSubmit,
    onCancel,
    source,
    setErrorType,
    redirectToHelpPage,
    consumerId,
    page
  }
) {
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [isOverSelectionLimit, setIsOverSelectionLimit] = useState(false);
  const [reachSelectionLimit, setReachSelectionLimit] = useState(false);
  const [availableSelections, setAvailableSelections] = useState(availableFeeds);

  const handleOnClick = () => {
    onSubmit(selectedAccounts);
  }

  const handleCancel = async () => {
    await onCancel();
    if (!!productRedirectUrl) {
      window.location.assign(productRedirectUrl.trim());
    } else {
      setErrorType('accountsNotConnected');
    }
  };

  const handleUpgrade = () => {
    window.open(subscriptionUpgradeUrl, '_blank').focus()
  }

  const handleManageFeed = () => {
    if (!_.isEmpty(productRedirectUrl) && productRedirectUrl !== 'null' && productRedirectUrl !== 'undefined') {
      window.open(productRedirectUrl, '_blank').focus()
    }
  }

  const updateButtonStatus = () => {
    const initButtonDisabled = page === 'ExistingEdit' ? !_.some(accounts, a => a.onboarded) : true;
    const accountsSelected = selectedAccounts.length > 0;
    const underFeedLimit = selectedAccounts.length <= availableFeeds || availableFeeds == null;
    setIsButtonDisabled(initButtonDisabled && !(accountsSelected && underFeedLimit))
  }

  const checkSelectionLimit = () => {
    const overFeedLimit = selectedAccounts.length > availableFeeds;
    if(overFeedLimit) {
      return setIsOverSelectionLimit(true)
    }
    setIsOverSelectionLimit(false)
  }

  const updateAvailableSelections = () => {
    const availableSelections = availableFeeds - selectedAccounts.length;
    setReachSelectionLimit(availableSelections === 0)
    setAvailableSelections(availableSelections)
  }

  useEffect(() => {
    updateButtonStatus();
    FullStory('trackEvent', {
      name: 'Onboard Finalise',
      properties: {
        source: source
      },
    })
  }, []);

  useEffect(() => {
    updateButtonStatus()
    checkSelectionLimit()
    updateAvailableSelections()
  }, [selectedAccounts]);

  SelectAccountsContainer.propTypes = {
    accounts: PropTypes.array,
    bankfeedLimit: PropTypes.number,
    availableFeeds: PropTypes.number,
    productRedirectUrl: PropTypes.string,
    subscriptionUpgradeUrl: PropTypes.string,
    onSubmit: PropTypes.func,
    onCancel: PropTypes.func,
    source: PropTypes.string,
    setErrorType: PropTypes.func,
    redirectToHelpPage: PropTypes.func,
    consumerId: PropTypes.string,
    page: PropTypes.string
  };

  const isFromOpenBanking = source?.toLowerCase() === "OpenBanking".toLowerCase();
  const isFromSolo = consumerId === "Solo";

  return (
    <div className={'page-block--sm animate'} style={{padding: '2.4rem'}}>
      {isFromSolo ? <PageHead title="Select accounts"/>: <PageHead title="Choose accounts"/>}
      <StickyHeader>
        <SelectionMessage
          availableFeeds={availableFeeds}
          availableSelections={availableSelections}
          bankfeedLimit={bankfeedLimit}
          isOverFeedLimit={isOverSelectionLimit}
          reachSelectionLimit={reachSelectionLimit}
          handleManageFeed={handleManageFeed}
          handleUpgrade={handleUpgrade}
          source={source}
          consumerId={consumerId}
        />
      </StickyHeader>
      <Card size={'xxl'}>
        <AccountsTable
          accounts={accounts}
          selectedAccounts={selectedAccounts}
          setSelectedAccounts={setSelectedAccounts}
          source={source}
          reachSelectionLimit={reachSelectionLimit}
          page={page}
        />
      </Card>
      <ButtonRow className="animate"
                 secondary={[isFromOpenBanking && !isFromSolo && helpUrls.GetHelpSettingUpBankUrl &&
                 <ButtonLink tone="info"
                             icon={<OpenExternalLinkIcon/>}
                             onClick={() => redirectToHelpPage("choose_account")}
                             key="help-link"
                             iconRight>Get help setting up a bank connection
                 </ButtonLink>]}
                 primary={[<Button
                   className="cancel-account-selection"
                   style={{marginBottom: '20px'}}
                   type="secondary"
                   onClick={handleCancel}
                   key="cancel-link"
                 >
                   Cancel
                 </Button>,
                   <Button
                     style={{marginBottom: '20px'}}
                     type="primary"
                     tone="brand"
                     onClick={handleOnClick}
                     disabled={isButtonDisabled}
                     key="connect-link"
                   >
                     Connect
                   </Button>]}
      >
      </ButtonRow>
    </div>
  )
}

export default SelectAccountsContainer;
